import { ref, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useSdk } from "@/composables/useSdk";
import { debounce } from "@/utils/debounce";
import type { UserProfile } from "@cc-consulting-nv/ccsdk";

// Search suggestion types
export interface SearchSuggestion {
  id: string;
  type: "user" | "audio" | "hashtag" | "query";
  text: string;
  subtext?: string;
  data?:
    | UserProfile
    | { name: string }
    | { ulid: string; title: string; username: string };
}

export interface UseSearchInputOptions {
  /** Minimum query length to trigger suggestions */
  minQueryLength?: number;
  /** Debounce delay in ms */
  debounceMs?: number;
  /** Callback when search is executed (Enter or suggestion selected) */
  onSearch?: (query: string) => void;
  /** Whether to navigate to search page on Enter (default: true) */
  navigateOnEnter?: boolean;
}

export function useSearchInput(options: UseSearchInputOptions = {}) {
  const {
    minQueryLength = 3,
    debounceMs = 200,
    onSearch,
    navigateOnEnter = true,
  } = options;

  const router = useRouter();
  const { sdk } = useSdk();

  // Refs
  const searchInputRef = ref<HTMLInputElement | null>(null);
  const searchQuery = ref("");
  const isInputFocused = ref(false);
  const showSuggestions = ref(false);
  const suggestions = ref<SearchSuggestion[]>([]);
  const isLoadingSuggestions = ref(false);
  const selectedSuggestionIndex = ref(-1);

  // Fetch suggestions from API
  async function fetchSuggestions() {
    const query = searchQuery.value.trim();
    if (query.length < minQueryLength) {
      suggestions.value = [];
      return;
    }

    isLoadingSuggestions.value = true;
    const newSuggestions: SearchSuggestion[] = [];

    try {
      // Fetch users, audio, and hashtags in parallel
      const [usersResult, audioResult, hashtagsResult] = await Promise.all([
        sdk.searchUsersAutocomplete(query).catch((e) => {
          console.error("[useSearchInput] User search failed:", e);
          return { data: [] as UserProfile[] };
        }),
        sdk.searchAudio(query).catch((e) => {
          console.error("[useSearchInput] Audio search failed:", e);
          return {
            data: [] as Array<{ ulid: string; title: string; username: string }>,
          };
        }),
        sdk.searchHashtags(query).catch((e) => {
          console.error("[useSearchInput] Hashtag search failed:", e);
          return { data: { items: [] as Array<{ name: string }> } };
        }),
      ]);

      // Extract users - handle both { data: [...] } and direct array responses
      const usersData = usersResult?.data;
      const users: UserProfile[] = Array.isArray(usersData) ? usersData : [];

      for (const u of users.slice(0, 3)) {
        newSuggestions.push({
          id: `user-${u.ulid}`,
          type: "user",
          text: u.name || u.displayName || u.username || "Unknown",
          subtext: `@${u.username || "unknown"}`,
          data: u,
        });
      }

      // Extract audio - handle both { data: [...] } and direct array responses
      const audioData = audioResult?.data;
      const audio = Array.isArray(audioData) ? audioData : [];

      for (const a of audio.slice(0, 3)) {
        newSuggestions.push({
          id: `audio-${a.ulid}`,
          type: "audio",
          text: a.title,
          subtext: `@${a.username}`,
          data: a,
        });
      }

      // Extract hashtags - handle nested { data: { items: [...] } } structure
      const hashtagItems = hashtagsResult?.data?.items;
      const hashtags = Array.isArray(hashtagItems) ? hashtagItems : [];

      for (const h of hashtags.slice(0, 2)) {
        newSuggestions.push({
          id: `hashtag-${h.name}`,
          type: "hashtag",
          text: `#${h.name}`,
          data: h,
        });
      }

      // Add query suggestion at the beginning
      if (query && !query.startsWith("#") && !query.startsWith("@")) {
        newSuggestions.unshift({
          id: `query-${query}`,
          type: "query",
          text: query,
          subtext: "Search all",
        });
      }

      suggestions.value = newSuggestions;
    } catch (error) {
      console.error("[useSearchInput] Failed to fetch suggestions:", error);
    } finally {
      isLoadingSuggestions.value = false;
    }
  }

  // Debounced suggestion fetch
  const debouncedFetchSuggestions = debounce(() => {
    fetchSuggestions();
  }, debounceMs);

  // Handle search input
  function handleSearchInput() {
    selectedSuggestionIndex.value = -1;
    if (searchQuery.value.trim().length >= minQueryLength) {
      showSuggestions.value = true;
      debouncedFetchSuggestions();
    } else {
      showSuggestions.value = false;
      suggestions.value = [];
    }
  }

  // Handle search focus
  function handleSearchFocus() {
    isInputFocused.value = true;
    if (
      searchQuery.value.trim().length >= minQueryLength &&
      suggestions.value.length > 0
    ) {
      showSuggestions.value = true;
    }
  }

  // Handle search blur
  function handleSearchBlur() {
    isInputFocused.value = false;
    // Delay hiding to allow click events on suggestions
    setTimeout(() => {
      showSuggestions.value = false;
    }, 200);
  }

  // Handle Enter key
  function handleSearchEnter() {
    if (
      selectedSuggestionIndex.value >= 0 &&
      suggestions.value[selectedSuggestionIndex.value]
    ) {
      selectSuggestion(suggestions.value[selectedSuggestionIndex.value]);
    } else if (searchQuery.value.trim()) {
      closeSuggestions();
      onSearch?.(searchQuery.value.trim());
      if (navigateOnEnter) {
        router.push({ path: "/search", query: { q: searchQuery.value.trim() } });
      }
    }
  }

  // Navigate suggestions with keyboard
  function navigateSuggestion(direction: number) {
    if (!showSuggestions.value || suggestions.value.length === 0) return;

    const newIndex = selectedSuggestionIndex.value + direction;
    if (newIndex < -1) {
      selectedSuggestionIndex.value = suggestions.value.length - 1;
    } else if (newIndex >= suggestions.value.length) {
      selectedSuggestionIndex.value = -1;
    } else {
      selectedSuggestionIndex.value = newIndex;
    }
  }

  // Close suggestions
  function closeSuggestions() {
    showSuggestions.value = false;
    selectedSuggestionIndex.value = -1;
  }

  // Select a suggestion
  function selectSuggestion(suggestion: SearchSuggestion) {
    closeSuggestions();

    if (suggestion.type === "user") {
      const u = suggestion.data as UserProfile;
      searchQuery.value = "";
      router.push(`/profile/${u.username}`);
      return;
    }

    if (suggestion.type === "audio") {
      const a = suggestion.data as { ulid: string };
      searchQuery.value = "";
      router.push(`/song/${a.ulid}`);
      return;
    }

    if (suggestion.type === "hashtag") {
      const text = suggestion.text;
      searchQuery.value = text;
      onSearch?.(text);
      if (navigateOnEnter) {
        router.push({ path: "/search", query: { q: text } });
      }
      return;
    }

    // Query type - go to search page or trigger callback
    const text = suggestion.text;
    onSearch?.(text);
    if (navigateOnEnter) {
      searchQuery.value = "";
      router.push({ path: "/search", query: { q: text } });
    }
  }

  // Get icon for suggestion type
  function getSuggestionIcon(type: SearchSuggestion["type"]): string {
    switch (type) {
      case "user":
        return "mdi-account-outline";
      case "audio":
        return "mdi-music-note";
      case "hashtag":
        return "mdi-pound";
      default:
        return "mdi-magnify";
    }
  }

  // Clear search
  function clearSearch() {
    searchQuery.value = "";
    suggestions.value = [];
    showSuggestions.value = false;
    selectedSuggestionIndex.value = -1;
  }

  // Focus the search input
  function focusInput() {
    searchInputRef.value?.focus();
  }

  // Cleanup on unmount
  onUnmounted(() => {
    debouncedFetchSuggestions.cancel();
  });

  return {
    // Refs
    searchInputRef,
    searchQuery,
    isInputFocused,
    showSuggestions,
    suggestions,
    isLoadingSuggestions,
    selectedSuggestionIndex,

    // Handlers
    handleSearchInput,
    handleSearchFocus,
    handleSearchBlur,
    handleSearchEnter,
    navigateSuggestion,
    closeSuggestions,
    selectSuggestion,
    getSuggestionIcon,
    clearSearch,
    focusInput,
  };
}
